/**
 * dt-dialog - Modal dialog with focus trap and keyboard dismissal
 * Features: open/close methods, ESC key, backdrop click, focus management
 *
 * Uses DOM node manipulation. The backdrop/panel structure is built once;
 * open/close toggles CSS classes and aria attributes only — child custom
 * elements are never destroyed and re-created.
 */
import { BaseElement } from '../base-element';

export class DtDialog extends BaseElement {
  static observedAttributes = ['open'];

  private _isOpen = false;
  private _previousActiveElement: HTMLElement | null = null;
  private _backdropEl: HTMLElement | null = null;
  private _panelEl: HTMLElement | null = null;
  private _built = false;

  get open(): boolean {
    return this._isOpen;
  }

  set open(value: boolean) {
    if (value !== this._isOpen) {
      if (value) {
        this.showDialog();
      } else {
        this.hideDialog();
      }
    }
  }

  connectedCallback(): void {
    if (!this._built) {
      this.buildDialog();
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.handleKeydown);
  }

  attributeChangedCallback(name: string, _oldValue: string, newValue: string): void {
    if (name === 'open') {
      this.open = newValue !== null && newValue !== 'false';
    }
  }

  showDialog(): void {
    if (this._isOpen) return;

    this._previousActiveElement = document.activeElement as HTMLElement;
    this._isOpen = true;
    this.toggleVisibility();

    document.addEventListener('keydown', this.handleKeydown);

    requestAnimationFrame(() => {
      this.focusFirstElement();
    });
  }

  closeDialog(): void {
    this.hideDialog();
  }

  private hideDialog(): void {
    if (!this._isOpen) return;

    this._isOpen = false;
    this.toggleVisibility();

    document.removeEventListener('keydown', this.handleKeydown);

    if (this._previousActiveElement) {
      this._previousActiveElement.focus();
      this._previousActiveElement = null;
    }

    this.emit('close');
  }

  private toggleVisibility(): void {
    if (!this._backdropEl || !this._panelEl) return;

    if (this._isOpen) {
      this._backdropEl.className =
        'backdrop fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 transition-opacity duration-150 opacity-100 visible';
      this._backdropEl.removeAttribute('aria-hidden');
      this._panelEl.className =
        'panel relative w-full max-w-lg max-h-[calc(100vh-4rem)] overflow-y-auto rounded-2xl bg-white shadow-2xl transform transition-transform duration-150 scale-100';
    } else {
      this._backdropEl.className =
        'backdrop fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 transition-opacity duration-150 opacity-0 invisible pointer-events-none';
      this._backdropEl.setAttribute('aria-hidden', 'true');
      this._panelEl.className =
        'panel relative w-full max-w-lg max-h-[calc(100vh-4rem)] overflow-y-auto rounded-2xl bg-white shadow-2xl transform transition-transform duration-150 scale-95';
    }
  }

  private handleKeydown = (e: KeyboardEvent): void => {
    if (!this._isOpen) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      this.hideDialog();
    }

    if (e.key === 'Tab') {
      const focusableElements = this.getFocusableElements();
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    }
  };

  private handleBackdropClick = (e: Event): void => {
    const target = e.target as HTMLElement;
    if (target.classList.contains('backdrop')) {
      this.hideDialog();
    }
  };

  private getFocusableElements(): HTMLElement[] {
    if (!this._panelEl) return [];

    return Array.from(
      this._panelEl.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
      )
    );
  }

  private focusFirstElement(): void {
    const focusable = this.getFocusableElements();
    if (focusable.length > 0) {
      focusable[0].focus();
    } else if (this._panelEl) {
      this._panelEl.setAttribute('tabindex', '-1');
      this._panelEl.focus();
    }
  }

  private buildDialog(): void {
    // Build content container and move user's children in first
    const contentEl = document.createElement('div');
    contentEl.className = 'content p-6 pt-12';
    this.adoptChildrenInto(contentEl);

    // Backdrop (starts closed)
    this._backdropEl = document.createElement('div');
    this._backdropEl.className =
      'backdrop fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 transition-opacity duration-150 opacity-0 invisible pointer-events-none';
    this._backdropEl.setAttribute('role', 'dialog');
    this._backdropEl.setAttribute('aria-modal', 'true');
    this._backdropEl.setAttribute('aria-hidden', 'true');
    this._backdropEl.addEventListener('click', this.handleBackdropClick);

    // Panel
    this._panelEl = document.createElement('div');
    this._panelEl.className =
      'panel relative w-full max-w-lg max-h-[calc(100vh-4rem)] overflow-y-auto rounded-2xl bg-white shadow-2xl transform transition-transform duration-150 scale-95';
    this._panelEl.setAttribute('role', 'document');

    // Close button
    const closeBtn = document.createElement('button');
    closeBtn.className =
      'close-btn absolute right-4 top-4 rounded-lg p-2 text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-900 focus:outline-hidden focus:ring-2 focus:ring-primary-500/50';
    closeBtn.type = 'button';
    closeBtn.setAttribute('aria-label', 'Close dialog');
    closeBtn.innerHTML =
      '<svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>';
    closeBtn.addEventListener('click', () => this.hideDialog());

    this._panelEl.appendChild(closeBtn);
    this._panelEl.appendChild(contentEl);
    this._backdropEl.appendChild(this._panelEl);

    this.appendChild(this._backdropEl);
    this._built = true;
  }
}

customElements.define('dt-dialog', DtDialog);
